

  
const content = `# OnData Encryption Tool Overview

The OnData Encryption Tool can be used to efficiently encrypt, decrypt, or obfuscate sensitive data in databases. It can also be used to apply any changes 
of the encryption settings to a database that runs in a production environment.  

The list of database engines that are currently supported by the OnData Encryption Tool includes:  
- Azure Cosmos DB
- DB2
- MariaDB
- MongoDB
- MySQL
- Oracle
- PostgreSQL
- Snowflake
- SQL Server

## 1. Operating Modes
The OnData Encryption Tool has four operating modes:
- Encryption, which includes the option of Obfuscation for the test data generation use case
- Decryption
- Apply Encryption Setting Changes
- Migrate and Encrypt, which is available for Oracle databases only

### 1.1 Encryption Mode
The **Encryption** operating mode is used to encrypt a database according to the encryption rules configured at the OnData Portal. You have an option to apply Obfuscation 
when running the OnData Encryption Tool in Encryption mode. 

- Encryption: Encrypt sensitive data such that the resulting encrypted values have the same format (data type) as that of the input values. The encrypted values can be decrypted with the correct encryption key.
- Obfuscation: If an obfuscation rule is specified for a database table column either at the OnData Portal, or in a local configuration file, the OnData Encryption Tool can 
obfuscate the column data based on the obfuscation rule. The OnData Encryption Tool will randomly pick a value from a list of values specified in the obfuscation rule. The 
list of values should meet the data validation rules or constraints in the database or related applications. The OnData Portal provides four types of obfuscation rules:
  - Table Column: Randomly pick a value from a related lookup table.
  - Value List: Randomly pick a value from a list of specific values.
  - Value Range: Randomly pick a value from a continuous range of values.
  - Clear Data: Clear the existing data in a column.

**Note**: Unlike encrypted values, the obfuscated values cannot be decrypted to the original values. Therefore, you should not use the Obfuscation feature directly 
on a database in your production environment. Instead, you may enable the Obfuscation feature when you would like to use the OnData Encryption Tool to generate test data 
for your lower environments, such as Development, Testing, Staging, or Training environments. In that case, you may restore a backup of your Production database and 
run the OnData Encryption Tool against the restored database instance.

### 1.2 Decryption Mode
The **Decryption** operating mode is used to decrypt a previously encrypted database. All obfuscation rules will be ignored.

### 1.3 Apply Encryption Setting Changes Mode
The **Apply Encryption Setting Changes** operating mode is used to apply encryption setting changes for a Production database. For more information 
about the **Apply Encryption Setting Changes** operating mode, please refer to the [**Apply Encryption Setting Changes**](reference_doc#securedb/ondata_encryption/apply_encryption_setting_changes)  

### 1.4 Migrate and Encrypt Mode
The **Migrate and Encrypt** mode only works for Oracle databases at this time. Essentially, you can configure the OnData Encryption Tool to migrate certain schemas and data 
from a **Source** database to a **Target** database, with encryption rules, and optionally obfuscation rules, applied as part of the migration. No change will be made to 
**Source** database. For more information about migrating and encrypting an Oracle database, please refer to 
[**Migrate and Encrypt Oracle Databases**](reference_doc#securedb/ondata_encryption/migrate_and_encrypt_oracle_database)  
  
## 2. Prerequisites for OnData Encryption Tool
Here are the prerequisites for installing and using the OnData Encryption Tool.  

### 2.1 Operating Systems
- Windows 10 or later, 64-bit edition
- Windows Server 2019 or later, 64-bit edition
- Linux Operating System, 64-bit edition

### 2.2 Runtimes for Windows Operating Systems
To run the Windows version of the OnData Encryption Tool that presents a user interface, the following runtime libraries are required. You may install such runtime 
libraries before installing the OnData Encryption Tool.  
- [Microsoft Edge WebView Runtime](http://go.microsoft.com/fwlink/p/?LinkId=2124703)
- [Microsoft .NET 8.0 Desktop Runtime for Windows x64](https://download.visualstudio.microsoft.com/download/pr/f1e7ffc8-c278-4339-b460-517420724524/f36bb75b2e86a52338c4d3a90f8dac9b/windowsdesktop-runtime-8.0.12-win-x64.exe)

### 2.3 Database Prerequisites
To run the OnData Encryption Tool with certain database engines, some specific configurations and permissions are required. Please refer to the below requirements 
listed for each database engine.

#### 2.3.1 SQL Server
- The TCP/IP network protocol needs to be enabled for the database server. You can confirm such configuration with the \`SQL Server Configuration Manager\`. 
Please refer to [this link](https://docs.microsoft.com/en-us/sql/database-engine/configure-windows/enable-or-disable-a-server-network-protocol?view=sql-server-ver15) 
for more details.

- A SQL Server database user with the following permissions assigned at a minimum:
  - db_datareader
  - db_datawriter
  - db_ddladmin
  - VIEW SERVER STATE

#### 2.3.2 Oracle
- An Oracle database user with the following permissions assigned at a minimum:
  - SELECT_CATALOG_ROLE
  - LOCK ANY TABLE
  - UPDATE ANY TABLE
  - INSERT ANY TABLE
  - SELECT ANY DICTIONARY
  - SELECT ANY TABLE
  - ALTER ANY TABLE
  - CREATE ANY TRIGGER
  - ALTER ANY TRIGGER
  - CREATE SESSION
  - DROP ANY TABLE
  - ALTER ANY MATERIALIZED VIEW
  - CREATE ANY TABLE
  - CREATE ANY INDEX
  - ALTER ANY INDEX
  - DROP ANY INDEX
  - CREATE ANY PROCEDURE
  - SELECT ANY SEQUENCE
  - CREATE ANY SEQUENCE
  - DROP ANY SEQUENCE
  - ALTER ANY SEQUENCE
  - EXECUTE ANY TYPE
  
- The OnData Encryption Tool can fully utilize the parallel processing capability of the database server. If the database server has many CPU cores, you may 
review and update the processes parameter. To view the current processes parameter, use the command: \`show parameter processes\`. If the parameter is low, you 
may increase it to a higher value, for example: \`alter system set processes=2000 scope=spfile\`

#### 2.3.3 MySQL or MariaDB
For MySQL or MariaDB databases, check the local_infile with this query: \`SHOW GLOBAL VARIABLES LIKE 'local_infile';\`
- If the value of the local_infile is set to ON, you don't need to do anything else.
- If the value of the local_infile is set to OFF, it means that data import from the client side is disabled. Please update the my.cnf file (for Mac and Linux operating systems) or the my.ini file (for Windows operating systems) with the following configuration. Once the configuration file is updated, save it and restart your MySQL/MariaDB server.

\`\`\`ini
[mysqld]
local_infile=ON
\`\`\`

Please refer to [this link](https://dev.mysql.com/doc/refman/8.0/en/load-data-local-security.html#load-data-local-configuration) for more details.  
  
For MySQL 8.x, the following privileges are required:  
- Database Privileges: TRIGGER, SHOW VIEW, INSERT, UPDATE, CREATE, DROP, ALTER  
- Global Privileges: SELECT, SET_USER_ID  
  
\`\`\`sql
CREATE USER '<database_username>' IDENTIFIED BY '<password>';
GRANT TRIGGER, SHOW VIEW, INSERT, UPDATE, CREATE, DROP, ALTER ON <database_name>.* TO '<database_username>';
GRANT SELECT, SET_USER_ID ON *.* TO '<database_username>';
\`\`\`
  
For MySQL 5.x and MariaDB, the following  privileges are required:  
- Database Privileges: SELECT, TRIGGER, SHOW VIEW, INSERT, UPDATE, CREATE, DROP, ALTER
- Global Privileges: SUPER, EXECUTE
  
\`\`\`sql
CREATE USER '<database_username>' IDENTIFIED BY '<password>';
GRANT SELECT, TRIGGER, SHOW VIEW, INSERT, UPDATE, CREATE, DROP, ALTER ON <database_name>.* TO 'database_username';
GRANT SUPER, EXECUTE ON *.* TO 'database_username';
\`\`\`
  
#### 2.3.4 PostgreSQL
For PostgreSQL databases, the database user needs to have the superuser, owner, or member of owner role for of all tables.  
  
#### 2.3.5 DB2
For DB2 databases, the database user needs to have the following roles:  
- Server instance-level authority: SYSMON
- Database authorities: CONNECT, CREATETAB, DATAACCESS
- Schema privileges: CREATEIN, ALTERIN
- Table privileges: CONTROL
- Tablespace privileges: USE
- workload privileges: USAGE
  
#### 2.3.6 MongoDB
- A MongoDB database user with the "readWrite" permissions assigned at a minimum. You may use the following scripts to assign the permission:

\`\`\`bash
db.grantRolesToUser(
   "{user_name}",
   [{role: "readWrite", db:"{database name}"}]
)
\`\`\`
- There are multiple limitations when processing MongoDB databases. Please refer to [MongoDB Encryption Limitations](reference_doc#securedb/ondata_encryption/mongodb_encryption_limitations) for more details.  

### 2.4 OnData Account with Sufficient Permissions
You need to have an active account with the OnData Platform in order to use this tool. The following permissions are required to use OnData Encryption Tool:
- Manage Backup Jobs
- Schedule Encryption and Obfuscation

If you would like to create encryption keys using the OnData Encryption Tool, you will need the following permission. But this is not required. The encryption keys 
can be created by System Administrators and Security Administrators at the OnData Portal.
- Key Management

## 3. Best Practices and Limitations
There are a number of best practices and limitations associated with encrypting sensitive data in databases. Please refer to [Encryption Best Practices and Limitations](reference_doc#securedb/ondata_encryption/encryption_best_practices_and_limitations) for more details.  
`
  
export default content;
